import { blue, grey } from '@mui/material/colors';
import { CheckRounded } from '@mui/icons-material';
import '@mui/material';
import { CircularProgress, Grid, Checkbox, Fade, Tooltip } from '@mui/material';
import classnames from 'classnames';
import { UPDATE_NOTIFICATIONS_CHANNEL_SUCCESS } from 'js/constants/actionTypes';
import { find, has, isEmpty, isNil, map } from 'lodash';
import RoutePrefixContext from 'new/contexts/RoutePrefixContext';
import { Accordion } from 'new/elements/Accordion';
import { Button } from 'new/elements/Button';
import { CenteredLoader } from 'new/elements/CenteredLoader';
import { Stack } from 'new/elements/Stack';
import { Tabs } from 'new/elements/Tabs';
import { TextField } from 'new/elements/TextField';
import { normalizeEmailList } from 'new/utils/normalizeEmailList';
import Typography from 'new/elements/TypographySupport';
import React from 'react';
import { EMAIL, SLACK, WEBHOOKS } from '../../constants/notificationChannels';
import { SUPPORT_KEY_NOTIFICATION } from '../../constants/supportPageKeys';
import { BannerDescription } from 'new/elements/BannerDescription';

import classes from './NotificationManager.module.scss';
import './NotificationManager.scss';

const description = 'Manage notification frequency and channels.';
const thumbDefault = {
  border: '2px solid white',
  boxShadow: 'none',
  width: 24,
  height: 24,
  marginTop: '-2px',
};
const styles = {
  titleRoot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    // marginTop: 16,
    backgroundColor: 'hsl(202,87%,12%)',
    // boxShadow: 'rgba(42, 63, 86, 0.1) 0px 0px 10px 0px',
    paddingTop: 14,
    paddingBottom: 14,
    margin: 0,
    position: 'sticky',
    top: 0,
    zIndex: 2,
    borderRadius: 0,
  },
  panel: {
    marginBottom: 20,
  },
  textArea: {
    display: 'flex',
    flexDirection: 'column',
  },
  heading: {
    paddingLeft: 20,
    marginBottom: 0,
    marginTop: 0,
    fontSize: 20,
    color: '#ff7436',
  },
  subheading: {
    fontSize: 12,
    paddingLeft: 20,
    color: '#cccccc',
  },
  slackButton: {
    margin: '12px 0',
  },
  tabsContainer: {
    padding: 20,
  },
  channelConfigContent: {
    padding: 20,
  },
  errorRow: {
    backgroundColor: 'rgba(233, 70, 72)',
    color: '#fff',
    fontSize: 11,
  },
  errorCol: {
    padding: '12px 20px',
  },
  thumbOff: {
    ...thumbDefault,
    backgroundColor: '#ADBDCB',
  },
  trackOff: {
    backgroundColor: '#ADBDCB',
  },
  thumbSwitched: {
    ...thumbDefault,
    backgroundColor: '#51D398',
  },
  trackSwitched: {
    backgroundColor: '#51D398',
  },
  checkboxStyle: {
    fill: blue[500],
  },
  checkboxDisabledStyle: {
    fill: grey[500],
  },
  checkboxDisabledContainer: {
    display: 'flex',
  },
  textStyle: {
    fontSize: 12,
  },
};

const CHANNEL_CONFIGURATIONS = [
  {
    category: EMAIL,
    name: 'Email Configuration',
  },
  {
    category: SLACK,
    name: 'Slack Configuration',
  },
  {
    category: WEBHOOKS,
    name: 'Webhook Configuration',
  },
];

const StyledCheckbox = Checkbox;
class NotificationsManager extends React.Component {
  static contextType = RoutePrefixContext;
  constructor(props) {
    super(props);

    this.state = {
      slackChannelURL: props.slackChannelURL,
      webhookChannel: props.webhookChannelURL,
      selectedCategoryTab: 'PLATFORM',
      selectedChannelConfigTab: EMAIL,
    };
  }

  componentDidMount() {
    if (!this.props.manager.notification_types) {
      this.props.loadNotificationsTypes();
    }
    if (!this.props.manager.notification_channels_enum_map) {
      this.props.loadNotificationsChannels();
    }
    this.props.loadNotificationsSettings('USER');

    this.props.setContextualSupport(SUPPORT_KEY_NOTIFICATION);
  }

  componentDidUpdate({
    channelActionType: prevChannelType,
    webhookChannelURL: prevWebhookChannelURL,
    slackChannelURL: prevSlackChannelURL,
  }) {
    const { channelActionType, webhookChannelURL, slackChannelURL } = this.props;
    (() => {
      if (channelActionType === prevChannelType) return;
      if (channelActionType !== UPDATE_NOTIFICATIONS_CHANNEL_SUCCESS) return;
      this.setState({ showSuccess: true });
      setTimeout(() => {
        this.setState({ showSuccess: false });
      }, 2000);
    })();

    if (webhookChannelURL !== prevWebhookChannelURL) {
      this.setState({ webhookChannel: webhookChannelURL });
    }

    if (slackChannelURL !== prevSlackChannelURL) {
      this.setState({ slackChannelURL });
    }
  }

  componentWillUnmount() {
    this.props.unsetContextualSupport(SUPPORT_KEY_NOTIFICATION);
  }

  handleChange = (stateKey, value) => {
    this.setState({
      [stateKey]: value,
    });
  };

  handleChangeSlackChannelURL = (e) => {
    this.setState({ slackChannelURL: e.target.value });
  };

  handleChangeWebhookChannelURL = (e) => {
    this.setState({ webhookChannel: e.target.value });
  };

  handleChangeEmails = (e) => {
    this.setState({ emails: e.target.value });
  };

  saveWebhookChannelURL = () => {
    this.props.updateNotificationChannel(this.props.webhookChannelId, WEBHOOKS, {
      url: this.state.webhookChannel,
    });
  };

  getEmailValue = () => {
    const { emails, notificationChannelsLoaded, currentUser } = this.props;
    if (this.state.emails !== undefined) return this.state.emails;
    if (!isNil(emails)) return emails;
    if (notificationChannelsLoaded) return currentUser.email;
    return '';
  };

  isEmailDisabled = () => !this.props.notificationChannelsLoaded || this.props.loadingChannelUpdate;

  saveEmails = () => {
    if (this.isEmailDisabled()) return;

    const normalized = normalizeEmailList(this.getEmailValue());
    this.setState({ emails: normalized });
    this.props.updateNotificationChannel(this.props.emailChannelId, EMAIL, {
      email: normalized,
    });
  };

  saveSlackURL = () => {
    this.props.updateNotificationChannel(this.props.slackChannelId, SLACK, {
      url: this.state.slackChannelURL,
    });
  };

  toggleData = (typeId, channel, value) => {
    this.props.updateNotificationManager('USER', this.props.currentUser.id, typeId, channel, value);
  };

  renderTabs(tabs, stateKey) {
    return (
      <div className={'notification-tabs'}>
        {map(tabs, (t) => {
          return (
            <div
              key={t.category}
              className={classnames('notification-tab', {
                selected: t.category === this.state[stateKey],
              })}
              onClick={() => {
                this.handleChange(stateKey, t.category);
              }}
            >
              {t.name}
            </div>
          );
        })}
      </div>
    );
  }

  renderTogglesHeaders() {
    let { notification_channels_enum } = this.props.manager;
    return (
      <Grid className="notification-setting-row" container>
        <Grid item xs className="notification-setting-item header-item">
          NOTIFICATION
        </Grid>
        {map(notification_channels_enum, (ch) => (
          <Grid item className="notification-setting-item header-item" key={ch + 'Title'} xs={1}>
            {ch}
          </Grid>
        ))}
      </Grid>
    );
  }

  renderCheckbox(typeId, channel, setting = {}) {
    let {
      manager: { notificationSettingsBeingSaved },
      slackChannelURL,
    } = this.props;
    let isSavingSetting = !!find(notificationSettingsBeingSaved, {
      typeId,
      channel,
    });

    if (isSavingSetting) {
      return (
        <div
          style={{
            height: '100%',
            display: 'flex',
            padding: '9px',
            alignItems: 'center',
            // paddingLeft: 20
          }}
        >
          <CircularProgress color="primary" size={18} disableShrink={true} />
        </div>
      );
    }

    if (channel === SLACK && !slackChannelURL) {
      return (
        <Tooltip title="Please complete Slack configuration in order to receive notifications on Slack.">
          <div>
            <StyledCheckbox checked={setting.status === 'ACTIVE'} disabled={true} />
          </div>
        </Tooltip>
      );
    }

    const isDisabled = !isEmpty(notificationSettingsBeingSaved);

    return (
      <StyledCheckbox
        checked={setting.status === 'ACTIVE'}
        disabled={isDisabled}
        onChange={() => {
          this.toggleData(typeId, channel, !(setting.status === 'ACTIVE'));
        }}
      />
    );
  }

  renderToggles() {
    let { byCategory, notification_types, notification_channels_enum, notification_settings } =
      this.props.manager;

    let typesIndexes = [];
    if (has(byCategory, this.state.selectedCategoryTab)) {
      typesIndexes = byCategory[this.state.selectedCategoryTab];
    }
    return map(typesIndexes, (ix) => {
      let type = notification_types[ix];
      return (
        <Grid
          container
          key={type.id}
          className="notification-setting-row body-row"
          alignItems="center"
        >
          <Grid item xs className="notification-setting-item">
            {type.name}
          </Grid>
          {map(notification_channels_enum, (ch) => {
            return (
              <Grid
                item
                xs={1}
                key={ch + '-' + type.id}
                className="notification-setting-item item-checkbox"
              >
                {this.renderCheckbox(type.id, ch, (notification_settings[type.id] || {})[ch])}
              </Grid>
            );
          })}
        </Grid>
      );
    });
  }

  onClickOpenSlack = () => {
    window.open(
      'https://my.slack.com/services/new/incoming-webhook/',
      '_blank',
      'noopener noreferrer',
    );
  };

  renderChannelConfiguration() {
    const { notificationChannelsLoaded, loadingChannelUpdate } = this.props;
    const { selectedChannelConfigTab, webhookChannel, slackChannelURL: slackChannel } = this.state;

    const webhookValue = webhookChannel || '';

    const slackValue = slackChannel || '';

    const emailValue = this.getEmailValue();
    const emailDisabled = this.isEmailDisabled();

    return (
      <Stack>
        <div className={classes.channelConfigContent}>
          <Accordion title="EMAIL">
            <Stack alignItems="flex-start">
              <Typography className={classes.channelDescription} variant="body2">
                Provide one or more comma-separated email addresses to be notified.
              </Typography>
              <Stack mt="16px" alignItems="flex-end" alignSelf="stretch" direction="row" flex="1">
                <TextField
                  id="emails"
                  size="small"
                  value={emailValue}
                  fullWidth
                  multiline
                  disabled={emailDisabled}
                  onChange={this.handleChangeEmails}
                  label="Emails to notify"
                />

                <Button disabled={emailDisabled} onClick={this.saveEmails} variant="text">
                  Save
                </Button>
              </Stack>
            </Stack>
          </Accordion>
        </div>
        <div className={classes.channelConfigContent}>
          <Accordion title="SLACK">
            <Stack alignItems="flex-start">
              <Typography className={classes.channelDescription} variant="body2">
                To create a Slack Webhook, configure it and then provide its URL.
              </Typography>
              <Button onClick={this.onClickOpenSlack} variant="contained">
                Configure Slack WebHook
              </Button>
              <Stack mt="16px" alignItems="flex-end" alignSelf="stretch" direction="row" flex="1">
                <TextField
                  id="slack"
                  size="small"
                  value={slackValue}
                  fullWidth
                  disabled={loadingChannelUpdate}
                  onChange={this.handleChangeSlackChannelURL}
                  label="Slack Webhook URL"
                />

                <Button disabled={loadingChannelUpdate} onClick={this.saveSlackURL} variant="text">
                  Save
                </Button>
              </Stack>
            </Stack>
          </Accordion>
        </div>
        <div className={classes.channelConfigContent}>
          <Accordion title="WEBHOOK">
            <Stack alignItems="flex-start">
              <Typography className={classes.channelDescription} variant="body2">
                Create an Incoming WebHook and provide its URL.
              </Typography>
              <Stack mt="16px" alignItems="flex-end" alignSelf="stretch" direction="row" flex="1">
                <TextField
                  id="webhook"
                  size="small"
                  value={webhookValue}
                  fullWidth
                  disabled={loadingChannelUpdate}
                  onChange={this.handleChangeWebhookChannelURL}
                  label="Webhook URL"
                />

                <Button
                  disabled={loadingChannelUpdate}
                  onClick={this.saveWebhookChannelURL}
                  variant="text"
                >
                  Save
                </Button>
              </Stack>
            </Stack>
          </Accordion>
        </div>
      </Stack>
    );
  }

  render() {
    const showMissingSlackConfigWarning = (() => {
      if (!this.props.notificationChannelsLoaded) {
        return false;
      }
      if (this.props.slackChannelURL) {
        return false;
      }
      const slackSettings = find(this.props.manager.notification_settings, (setting) =>
        has(setting, SLACK),
      )?.SLACK;
      return slackSettings && slackSettings.status === 'ACTIVE';
    })();

    const { loadingChannelUpdate, channelActionType, loadingSettings } = this.props;

    return (
      <div className={classes.rootContainer}>
        <BannerDescription title="Notification Settings" description={description} />

        <div style={styles.tabsContainer} className={classes.container}>
          <div style={styles.panel} className={classes.panel}>
            <div className={classes.notificationSettings}>
              <div style={{ paddingTop: 0 }} className={classes.notificationTabs}>
                <Tabs
                  tabs={this.props.manager.categories.map((t) => ({
                    label: t.name,
                    value: t.category,
                  }))}
                  onChange={(e, value) => this.handleChange('selectedCategoryTab', value)}
                  value={this.state.selectedCategoryTab}
                />
              </div>
              {showMissingSlackConfigWarning && (
                <div style={styles.errorRow}>
                  <div style={styles.errorCol}>
                    Please complete Slack configuration in order to receive notifications on Slack.
                  </div>
                </div>
              )}
              <div className={classes.togglesContainer}>
                {this.renderTogglesHeaders()}
                {this.renderToggles()}
              </div>
              {loadingSettings ? <CenteredLoader height={100} /> : null}
            </div>
          </div>
          <div>
            <Stack
              direction="row"
              gap="16px"
              alignItems="center"
              style={{ fontWeight: 'var(--font-weight-medium)', fontSize: 'var(--font-size-base)' }}
              className={classes.channelTitleContainer}
            >
              Channel Settings
              {loadingChannelUpdate && <CircularProgress size="16px" />}
              <Fade in={this.state.showSuccess}>
                <Typography variant="caption" className={classes.savedText}>
                  Saved <CheckRounded />
                </Typography>
              </Fade>
            </Stack>
            {this.renderChannelConfiguration()}
          </div>
        </div>
      </div>
    );
  }
}
export default NotificationsManager;
